import React from 'react';
import { createPortal } from 'react-dom';
import {
  HiOutlineAdjustmentsHorizontal,
  HiOutlineChatBubbleLeftRight,
  HiOutlineChevronDown,
  HiOutlineComputerDesktop,
  HiOutlineGlobeAlt,
  HiOutlineLockClosed,
  HiOutlineMicrophone,
  HiOutlineQuestionMarkCircle,
  HiOutlineVideoCamera,
  HiOutlineXMark,
} from 'react-icons/hi2';

const STORAGE_KEY = 'preflight-help-language';
const LANGUAGE_CODES = ['en', 'tr', 'fr', 'de', 'ar'];
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'ar', label: 'العربية' },
];

const HELP_COPY = {
  en: {
    title: 'Quick Help',
    subtitle: 'Quick fixes for camera and microphone issues.',
    items: {
      camera: { title: "Camera isn't working", answer: [
        'Make sure your camera is connected.',
        'Allow camera access in your browser.',
        'Close apps that may be using your camera.',
        'Try another camera from the device list.',
      ] },
      microphone: { title: "Microphone isn't working", answer: [
        'Make sure your microphone is connected.',
        'Allow microphone access in your browser.',
        'Close apps that may be using your microphone.',
        'Try another microphone from the device list.',
      ] },
      permissions: { title: 'I blocked permissions', answer: 'If you previously blocked access, click the camera/lock icon near your browser\'s address bar and allow Camera and Microphone access. Then retry the device check.' },
      video: { title: 'Video is black', answer: 'Close other apps using your camera, then select another camera if available. If the preview is still black, retry the device check.' },
      device: { title: 'Wrong device selected', answer: 'Use the Camera and Microphone dropdowns on this page to choose the devices you want to use.' },
      help: { title: 'Still need help?', answer: 'Refresh or restart your browser and return to this page. Make sure no other video-call application is using your camera or microphone. If nothing works, you can take the test on your cellphone using the same link.' },
    },
  },
  tr: {
    title: 'Hızlı Yardım',
    subtitle: 'Kamera ve mikrofon sorunları için hızlı çözümler.',
    items: {
      camera: { title: 'Kameram çalışmıyor', answer: [
        'Kameranızın bağlı olduğundan emin olun.',
        'Tarayıcınızda kamera iznine izin verin.',
        'Kamerayı kullanan diğer uygulamaları kapatın.',
        'Listeden başka bir kamera seçmeyi deneyin.',
      ] },
      microphone: { title: 'Mikrofonum çalışmıyor', answer: [
        'Mikrofonunuzun bağlı olduğundan emin olun.',
        'Tarayıcınızda mikrofon iznine izin verin.',
        'Mikrofonu kullanan diğer uygulamaları kapatın.',
        'Listeden başka bir mikrofon seçmeyi deneyin.',
      ] },
      permissions: { title: 'İzinleri engelledim', answer: 'Daha önce erişimi engellediyseniz, tarayıcınızın adres çubuğundaki kamera veya kilit simgesine tıklayın ve Kamera ile Mikrofon izinlerini açın. Ardından cihaz kontrolünü tekrar deneyin.' },
      video: { title: 'Video siyah görünüyor', answer: 'Kamerayı kullanan diğer uygulamaları kapatın. Varsa başka bir kamera seçin. Görüntü hâlâ siyahsa cihaz kontrolünü tekrar deneyin.' },
      device: { title: 'Yanlış cihaz seçildi', answer: 'Bu sayfadaki Kamera ve Mikrofon menülerinden kullanmak istediğiniz cihazları seçin.' },
      help: { title: 'Hâlâ yardıma mı ihtiyacınız var?', answer: 'Tarayıcınızı yenileyin veya yeniden başlatın ve bu sayfaya geri dönün. Başka bir görüntülü görüşme uygulamasının kameranızı veya mikrofonunuzu kullanmadığından emin olun. Hiçbir çözüm işe yaramazsa aynı bağlantıyı kullanarak testi cep telefonunuzdan yapabilirsiniz.' },
    },
  },
  fr: {
    title: 'Aide rapide',
    subtitle: 'Solutions rapides aux problèmes de caméra et de microphone.',
    items: {
      camera: { title: 'Ma caméra ne fonctionne pas', answer: [
        'Vérifiez que votre caméra est connectée.',
        "Autorisez l'accès à la caméra dans votre navigateur.",
        'Fermez les applications utilisant votre caméra.',
        'Essayez une autre caméra dans la liste.',
      ] },
      microphone: { title: 'Mon microphone ne fonctionne pas', answer: [
        'Vérifiez que votre microphone est connecté.',
        "Autorisez l'accès au microphone dans votre navigateur.",
        'Fermez les applications utilisant votre microphone.',
        'Essayez un autre microphone dans la liste.',
      ] },
      permissions: { title: "J'ai bloqué les autorisations", answer: "Si vous avez bloqué l'accès, cliquez sur l'icône caméra ou cadenas près de la barre d'adresse de votre navigateur et autorisez la caméra et le microphone. Réessayez ensuite la vérification." },
      video: { title: 'La vidéo est noire', answer: "Fermez les autres applications utilisant votre caméra, puis essayez une autre caméra si elle est disponible. Si l'image reste noire, relancez la vérification." },
      device: { title: 'Mauvais appareil sélectionné', answer: 'Utilisez les menus Caméra et Microphone de cette page pour choisir les appareils souhaités.' },
      help: { title: "Besoin d'aide ?", answer: "Actualisez ou redémarrez votre navigateur puis revenez sur cette page. Vérifiez qu'aucune autre application d'appel vidéo n'utilise la caméra ou le microphone. Si rien ne fonctionne, vous pouvez passer le test sur votre téléphone portable avec le même lien." },
    },
  },
  de: {
    title: 'Schnelle Hilfe',
    subtitle: 'Schnelle Lösungen für Kamera- und Mikrofonprobleme.',
    items: {
      camera: { title: 'Kamera funktioniert nicht', answer: [
        'Prüfen Sie, ob Ihre Kamera verbunden ist.',
        'Erlauben Sie den Kamerazugriff im Browser.',
        'Schließen Sie andere Apps, die die Kamera verwenden.',
        'Wählen Sie eine andere Kamera aus der Liste.',
      ] },
      microphone: { title: 'Mikrofon funktioniert nicht', answer: [
        'Prüfen Sie, ob Ihr Mikrofon verbunden ist.',
        'Erlauben Sie den Mikrofonzugriff im Browser.',
        'Schließen Sie andere Apps, die das Mikrofon verwenden.',
        'Wählen Sie ein anderes Mikrofon aus der Liste.',
      ] },
      permissions: { title: 'Berechtigungen blockiert', answer: 'Wenn Sie den Zugriff zuvor blockiert haben, klicken Sie auf das Kamera- oder Schloss-Symbol neben der Adressleiste und erlauben Sie Kamera und Mikrofon. Starten Sie danach die Geräteprüfung erneut.' },
      video: { title: 'Video bleibt schwarz', answer: 'Schließen Sie andere Apps, die Ihre Kamera verwenden. Wählen Sie danach, falls verfügbar, eine andere Kamera. Bleibt das Bild schwarz, starten Sie die Geräteprüfung erneut.' },
      device: { title: 'Falsches Gerät ausgewählt', answer: 'Wählen Sie über die Kamera- und Mikrofon-Menüs auf dieser Seite die gewünschten Geräte aus.' },
      help: { title: 'Weitere Hilfe?', answer: 'Aktualisieren oder starten Sie Ihren Browser neu und kehren Sie zu dieser Seite zurück. Stellen Sie sicher, dass keine andere Video-App Ihre Kamera oder Ihr Mikrofon verwendet. Wenn nichts funktioniert, können Sie den Test über denselben Link auf Ihrem Mobiltelefon ablegen.' },
    },
  },
  ar: {
    title: 'مساعدة سريعة',
    subtitle: 'حلول سريعة لمشاكل الكاميرا والميكروفون.',
    items: {
      camera: { title: 'الكاميرا لا تعمل', answer: [
        'تأكد من توصيل الكاميرا.',
        'اسمح للمتصفح بالوصول إلى الكاميرا.',
        'أغلق التطبيقات الأخرى التي تستخدم الكاميرا.',
        'جرّب اختيار كاميرا أخرى من القائمة.',
      ] },
      microphone: { title: 'الميكروفون لا يعمل', answer: [
        'تأكد من توصيل الميكروفون.',
        'اسمح للمتصفح بالوصول إلى الميكروفون.',
        'أغلق التطبيقات الأخرى التي تستخدم الميكروفون.',
        'جرّب اختيار ميكروفون آخر من القائمة.',
      ] },
      permissions: { title: 'لقد حظرت الأذونات', answer: 'إذا سبق أن حظرت الوصول، اضغط على رمز الكاميرا أو القفل بجوار شريط عنوان المتصفح واسمح بالوصول إلى الكاميرا والميكروفون. ثم أعد محاولة فحص الأجهزة.' },
      video: { title: 'الفيديو أسود', answer: 'أغلق التطبيقات الأخرى التي تستخدم الكاميرا، ثم جرّب كاميرا أخرى إذا كانت متاحة. إذا بقيت الصورة سوداء، أعد محاولة فحص الأجهزة.' },
      device: { title: 'تم اختيار جهاز خاطئ', answer: 'استخدم قائمتي الكاميرا والميكروفون في هذه الصفحة لاختيار الأجهزة التي تريد استخدامها.' },
      help: { title: 'هل ما زلت بحاجة إلى مساعدة؟', answer: 'حدّث المتصفح أو أعد تشغيله ثم ارجع إلى هذه الصفحة. تأكد من عدم استخدام تطبيق مكالمات فيديو آخر للكاميرا أو الميكروفون. إذا لم ينجح أي حل، يمكنك إجراء الاختبار على هاتفك المحمول باستخدام الرابط نفسه.' },
    },
  },
};

const FAQ_ITEMS = [
  { id: 'camera', Icon: HiOutlineVideoCamera },
  { id: 'microphone', Icon: HiOutlineMicrophone },
  { id: 'permissions', Icon: HiOutlineLockClosed },
  { id: 'video', Icon: HiOutlineComputerDesktop },
  { id: 'device', Icon: HiOutlineAdjustmentsHorizontal },
  { id: 'help', Icon: HiOutlineChatBubbleLeftRight },
];

function getInitialLanguage() {
  if (typeof window === 'undefined') return 'en';

  try {
    const savedLanguage = window.localStorage.getItem(STORAGE_KEY);
    if (LANGUAGE_CODES.includes(savedLanguage)) return savedLanguage;
  } catch (_storageError) {
    // Continue with the browser language when storage is unavailable.
  }

  const browserLanguage = (window.navigator.language || 'en').toLowerCase().split('-')[0];
  return LANGUAGE_CODES.includes(browserLanguage) ? browserLanguage : 'en';
}

function PreflightHelp({ camStatus, micStatus }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [language, setLanguage] = React.useState(getInitialLanguage);
  const [openFaq, setOpenFaq] = React.useState(null);
  const [languageMenuOpen, setLanguageMenuOpen] = React.useState(false);
  const helpButtonRef = React.useRef(null);
  const dialogRef = React.useRef(null);
  const languageMenuRef = React.useRef(null);
  const languageMenuOpenRef = React.useRef(false);
  const copy = HELP_COPY[language];
  const isRtl = language === 'ar';

  languageMenuOpenRef.current = languageMenuOpen;

  const openHelp = () => {
    setOpenFaq(camStatus !== 'granted' ? 'camera' : micStatus !== 'granted' ? 'microphone' : null);
    setIsOpen(true);
  };

  const closeHelp = React.useCallback(() => {
    setLanguageMenuOpen(false);
    setIsOpen(false);
  }, []);

  React.useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const returnFocusTarget = helpButtonRef.current;
    document.body.style.overflow = 'hidden';
    const focusFrame = window.requestAnimationFrame(() => {
      const firstButton = dialogRef.current && dialogRef.current.querySelector('button');
      if (firstButton) firstButton.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (languageMenuOpenRef.current) {
          setLanguageMenuOpen(false);
        } else {
          closeHelp();
        }
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll('button:not([disabled]), [href], select, [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialogRef.current.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (returnFocusTarget) returnFocusTarget.focus();
    };
  }, [closeHelp, isOpen]);

  React.useEffect(() => {
    if (!isOpen || !languageMenuOpen) return undefined;
    const closeLanguageMenu = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setLanguageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', closeLanguageMenu);
    document.addEventListener('touchstart', closeLanguageMenu);
    return () => {
      document.removeEventListener('mousedown', closeLanguageMenu);
      document.removeEventListener('touchstart', closeLanguageMenu);
    };
  }, [isOpen, languageMenuOpen]);

  const selectLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    setLanguageMenuOpen(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, nextLanguage);
    } catch (_storageError) {
      // The selection still works for this visit when storage is unavailable.
    }
  };

  const modal = isOpen && typeof document !== 'undefined' ? createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-slate-950/50 p-3"
      onClick={(event) => { if (event.target === event.currentTarget) closeHelp(); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-help-title"
        aria-describedby="quick-help-subtitle"
        dir={isRtl ? 'rtl' : 'ltr'}
        tabIndex={-1}
        className="flex max-h-[calc(100vh-24px)] w-[calc(100vw-24px)] max-w-[440px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-2xl shadow-slate-950/25"
        style={{ maxHeight: 'calc(100dvh - 24px)' }}
      >
        <div className="flex flex-shrink-0 items-start gap-2 border-b border-slate-200 p-3">
          <span className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-700" aria-hidden="true">
            <HiOutlineQuestionMarkCircle className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1 pt-1" style={{ textAlign: 'start' }}>
            <h2 id="quick-help-title" className="break-words text-lg font-extrabold leading-6 text-slate-950">{copy.title}</h2>
            <p id="quick-help-subtitle" className="mt-0.5 text-xs leading-5 text-slate-500">{copy.subtitle}</p>
          </div>
          <div className="flex flex-shrink-0 items-start gap-1">
            <div ref={languageMenuRef} className="relative">
              <button
                type="button"
                aria-label="Change help language"
                aria-haspopup="menu"
                aria-expanded={languageMenuOpen}
                onClick={() => setLanguageMenuOpen((current) => !current)}
                className="inline-flex min-h-[44px] items-center gap-1 rounded-xl px-2 text-xs font-bold uppercase text-blue-700 transition-colors hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              >
                <HiOutlineGlobeAlt className="h-4 w-4" aria-hidden="true" />
                {language}
                <HiOutlineChevronDown className={`h-3.5 w-3.5 transition-transform motion-reduce:transition-none ${languageMenuOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {languageMenuOpen && (
                <div role="menu" className={`absolute top-full z-10 mt-1 w-36 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 text-sm shadow-lg ${isRtl ? 'left-0' : 'right-0'}`}>
                  {LANGUAGES.map((option) => (
                    <button
                      key={option.code}
                      type="button"
                      role="menuitemradio"
                      aria-checked={language === option.code}
                      onClick={() => selectLanguage(option.code)}
                      className={`flex min-h-[44px] w-full items-center px-3 py-2 transition-colors hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${language === option.code ? 'bg-blue-50 font-bold text-blue-800' : 'text-slate-700'}`}
                      style={{ textAlign: 'start' }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              aria-label="Close help"
              onClick={closeHelp}
              className="flex h-11 w-11 items-center justify-center rounded-xl text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            >
              <HiOutlineXMark className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain p-3">
          {FAQ_ITEMS.map(({ id, Icon }) => {
            const item = copy.items[id];
            const isExpanded = openFaq === id;
            const buttonId = `preflight-help-${id}-button`;
            const panelId = `preflight-help-${id}-panel`;
            return (
              <div key={id} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <button
                  id={buttonId}
                  type="button"
                  aria-expanded={isExpanded}
                  aria-controls={panelId}
                  onClick={() => setOpenFaq(isExpanded ? null : id)}
                  className="flex min-h-[52px] w-full items-center gap-3 px-3 py-2.5 text-slate-800 transition-colors hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                  style={{ textAlign: 'start' }}
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700" aria-hidden="true">
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="min-w-0 flex-1 break-words text-sm font-semibold leading-5">{item.title}</span>
                  <HiOutlineChevronDown className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform motion-reduce:transition-none ${isExpanded ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
                {isExpanded && (
                  <div id={panelId} role="region" aria-labelledby={buttonId} className="border-t border-slate-100 bg-slate-50 px-3 pb-3 pt-2.5 text-sm leading-5 text-slate-600" style={{ textAlign: 'start' }}>
                    {Array.isArray(item.answer) ? (
                      <ol className="list-decimal space-y-1" style={{ paddingInlineStart: '1.25rem' }}>
                        {item.answer.map((step) => <li key={step}>{step}</li>)}
                      </ol>
                    ) : <p>{item.answer}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        ref={helpButtonRef}
        type="button"
        aria-label="Open troubleshooting help"
        onClick={openHelp}
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl px-2.5 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        <HiOutlineQuestionMarkCircle className="h-5 w-5" aria-hidden="true" />
        <span>Help</span>
      </button>
      {modal}
    </>
  );
}

export default PreflightHelp;
